import { AlertTitle, HStack, useDisclosure } from '@chakra-ui/react';
import { useQuery } from 'hooks/useCustomQuery';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { redirect, useLoaderData } from 'react-router-dom';

import { visibilityGroupsQuery } from '@tcap/data-router/loaders';
import { ACTIONS, useVisibilityGroup } from 'context/VisibilityGroupsContext';

import { Alert, AlertIcon, Text } from '@/components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import { Flex, Button, Tooltip } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { AddVgModal, DeleteVgModal, LeaveVgModal } from './modal/';
import { EditIcon } from '@chakra-ui/icons';
import { TbTrash } from 'react-icons/tb';
import walkthroughIds from '../walkthroughIds';
import ActionMenu from 'components/presentational/ActionMenu';
import { LeaveIcon } from 'components/v4/Icon/LeaveIcon';

function VisibilityGroupsHeader({ onAdd }) {
    return (
        <HStack spacing={2} paddingLeft={5}>
            <Tooltip
                label="Add Visibility Group"
                placement="bottom"
                hasArrow
                portalProps={{ containerRef: undefined }}>
                <div
                    style={{
                        position: 'relative',
                        display: 'inline-block',
                    }}>
                    <Button size="sm" width="160px" onClick={onAdd}>
                        + Add Visibility Group
                    </Button>
                </div>
            </Tooltip>
        </HStack>
    );
}

function VisibilityGroupsIndex() {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isDeleteVgModalOpen,
        onOpen: onDeleteVgModalOpen,
        onClose: onDeleteVgModalClose,
    } = useDisclosure();
    const {
        isOpen: isLeaveVgModalOpen,
        onOpen: onLeaveVgModalOpen,
        onClose: onLeaveVgModalClose,
    } = useDisclosure();
    const tableRef = useRef(null);
    const initialData = useLoaderData();
    const [, dispatchVG] = useVisibilityGroup();
    const [vg, setVg] = useState(null);
    const { requiredScope } = useSelector((state) => state.login);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const {
        visibilityGroups: { ufEditButton, ufDeleteButton, ufLeaveButton },
    } = walkthroughIds;
    const {
        data: visibilityGroupsData,
        isRefetching,
        isLoading,
        refetch,
        error,
    } = useQuery({
        ...visibilityGroupsQuery(),
        onSuccess: async (data) => {
            dispatchVG({
                type: ACTIONS.FETCH,
                payload: data,
            });

            return data;
        },
        enabled: requiredScope >= 40,
        staleTime: 500,
        initialData,
    });

    // AG Grid columns for visibility groups
    const agGridColumns = [
        { headerName: 'Name', field: 'name', width: 200 },
        { headerName: 'Description', field: 'description', width: 300 },
        { headerName: 'Members', field: 'assignedMembers', width: 200 },
        {
            lockPosition: 'right',
            headerName: 'Action',
            maxWidth: 100,
            filter: false,
            suppressMovable: true,
            lockVisible: true,
            suppressColumnsToolPanel: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            enableRowGroup: false,
            resizable: false,
            sortable: false,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
            }),
            cellRenderer: (params) => {
                const rowData = params.data;
                const actionItems = [
                    {
                        label: 'Manage',
                        onClick: () => {
                            if (typeof params.context?.onEdit === 'function') {
                                params.context.onEdit(rowData);
                            }
                        },
                        icon: EditIcon,
                        dataAttributes: {
                            'data-walkthroughid': `${ufEditButton}`,
                        },
                    },
                    {
                        label: 'Delete',
                        onClick: () => {
                            if (
                                typeof params.context?.onDelete === 'function'
                            ) {
                                params.context.onDelete(rowData);
                            }
                        },
                        icon: TbTrash,
                        dataAttributes: {
                            'data-walkthroughid': `${ufDeleteButton}`,
                        },
                    },
                    {
                        label: 'Leave',
                        onClick: () => {
                            if (typeof params.context?.onLeave === 'function') {
                                params.context.onLeave(rowData);
                            }
                        },
                        icon: LeaveIcon,
                        dataAttributes: {
                            'data-walkthroughid': `${ufLeaveButton}`,
                        },
                    },
                ];

                return <ActionMenu actionItems={actionItems} />;
            },
        },
    ];

    // Handlers for modals
    const handleEdit = (rowData) => {
        if (rowData && rowData.id) {
            navigate(`/visibility-groups/${rowData.id}/edit`);
        }
    };
    const handleDelete = (rowData) => {
        setVg(rowData);
        onDeleteVgModalOpen();
    };
    const handleLeave = (rowData) => {
        setVg(rowData);
        onLeaveVgModalOpen();
    };

    // Table data and loading state
    const tableData = visibilityGroupsData || [];
    const tableError = error ? 'Error loading table data' : '';

    if (requiredScope < 40) {
        throw redirect('/dashboard-services');
    }

    if (!currentCompany) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>
                    No Partner / Company / Wholesaler Selected
                </AlertTitle>
                <Text>Please select a wholesaler and a partner / company.</Text>
            </Alert>
        );
    }

    return (
        <section aria-label="visibilityGroups table">
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                Visibility Groups
            </Text>
            {tableError && (
                <div style={{ color: 'red', margin: 8 }}>{tableError}</div>
            )}
            <AGGridMain
                gridRef={tableRef}
                tableData={{ columns: agGridColumns, data: tableData }}
                titleName="Visibility Groups"
                refetch={refetch}
                extraComponents={
                    <Flex gap="4">
                        <VisibilityGroupsHeader onAdd={onOpen} />
                    </Flex>
                }
                isLoading={isLoading}
                isRefetching={isRefetching}
                context={{
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onLeave: handleLeave,
                }}
            />
            <AddVgModal onClose={onClose} isOpen={isOpen} />
            <DeleteVgModal
                onClose={onDeleteVgModalClose}
                onOpen={onDeleteVgModalOpen}
                isOpen={isDeleteVgModalOpen}
                vg={vg}
            />
            <LeaveVgModal
                onClose={onLeaveVgModalClose}
                isOpen={isLeaveVgModalOpen}
                vg={vg}
            />
        </section>
    );
}

export default VisibilityGroupsIndex;
